/**
 * Implements a tickers panel with search
 */
import { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { AutoSizer, List } from 'react-virtualized';
import { ITicker, enums, helpers } from '@kattana-io/common';
import {
  actionAddWatchlist,
  actionRemoveWatchlist,
} from '../../../../actions/watchlist';
import {
  actionTickersNextPageModal,
  actionUpdateModal,
  actionUpdateModalParams,
} from '../../../../actions/modal_search';
import { actionCloseModal } from '../../../../actions/modal';
import { EExchangeName } from '../../../../reducers/modalSearch';
import {
  getPrice,
  formatLiquidity,
  formatBaseVolume,
  defaultTickersSort,
  ISort,
} from '../helper';
import ExchangesSelector from './ExchangesSelector';
import ExchangeIcon from '../ExchangeIcon';
import { Column, CEX_columns, DEX_columns } from './Column';
import Loader from './Loader';
import TabsSelector from '../TabsSelector';
import { IColumn } from '../columns';
import { formatAddress, formatAPY } from '../../../../helpers/formatters';
import Text from '../../../../atoms/Text';
import useTicker from '../../../../hooks/useTicker';
import { IRates } from '../../../../reducers/rates';
import Resizer from './Resizer';
import PairIcons from '../../../../molecules/PairIcons';
import { IStore } from '../../../../reducers';
import { switchCurrentTicker } from '../../../../Authlogic';

import './styles.scss';

export const LS_TICKERS_HEIGHT = 'kattana-simple.tickers_modal_height';

interface ITickersPanelProps {
  tickers: any;
  watchlistTickers: ITicker[];
  // currentKey: string;
  // currentAddress: string;
  currentId: number;
  search: string;
  exchanges: EExchangeName[];
  items: ITicker[];
  loading: boolean;
  isNextPageLoading: boolean;
  page: number;
  rates: IRates;
  kind: number;
  actionRemoveWatchlist: Function;
  actionAddWatchlist: Function;
  actionUpdateModal: (value: any) => void;
  actionUpdateModalParams: (exchange: any) => void;
  actionTickersNextPageModal: Function;
  actionCloseModal: () => void;
}

const TickersPanel = (props: ITickersPanelProps) => {
  const { items, kind } = props;
  const [search, setSearch] = useState();
  const [loading, setLoading] = useState(false);
  const [watchlistViewIds, setWatchlistViewIds] = useState<number[]>([]);
  const inputRef = useRef(null);

  useEffect(() => {
    props.actionUpdateModalParams({ search: search || '' });
    // eslint-disable-next-line
  }, [search]);

  const [sort, onSelectSort] = useState<null | ISort>(defaultTickersSort);

  const onTickersUpdate = () => props.actionUpdateModal({ tickers: items });

  const setExchange = (exchanges: string[]) =>
    props.actionUpdateModalParams({ exchanges });

  /**
   * if items change, then put them into redux
   */
  useEffect(() => {
    if (items !== props.items) {
      onTickersUpdate();
    }
    // eslint-disable-next-line
  }, [items]);

  /**
   * Sort trigger call to server
   */
  useEffect(() => {
    if (sort) {
      props.actionUpdateModalParams({ sort });
    }
    // eslint-disable-next-line
  }, [sort]);

  useEffect(() => {
    (inputRef as any).current.focus();
  }, []);

  useEffect(() => {
    if (watchlistViewIds.length === 0) {
      const ids = props.watchlistTickers.map((ticker) => ticker.id);
      setWatchlistViewIds(ids);
    }
  }, [props.watchlistTickers]);

  useEffect(() => {
    let timer: any;
    if (!props.loading) {
      timer = setTimeout(() => {
        setLoading(props.loading);
      }, 300);
    } else {
      setLoading(props.loading);
    }

    return () => window.clearTimeout(timer);
  }, [props.loading]);

  /**
   *
   * @param e - event
   * loads more rows if scrolled ${scrollPercentage} * 100% of content
   */
  const onScroll = async (e: any) => {
    const scrollPercentage = 0.9;
    if (e.clientHeight + e.scrollTop > e.scrollHeight * scrollPercentage) {
      if (props.items.length % 100 === 0 && !props.isNextPageLoading) {
        // if length % 100 !== 0 it means that the last loaded page had less than 100 props.items and it was the last generally
        props.actionTickersNextPageModal({
          exchanges: props.exchanges,
          search: props.search,
        });
      }
    }
  };

  /**
   * On item clicked
   * @param item
   */
  const onListItemClicked = (data: ITicker) => {
    let [base, quote] = data.name.split('/');
    /**
     * Quote may include pair Address
     */
    if (quote.includes('@')) {
      [quote] = quote.split('@');
    }
    switchCurrentTicker(data);
    props.actionCloseModal();
  };

  /**
   * Add or remove item from watchlist
   * @param symbol
   * @param inlist
   * @param last
   * @param change
   */
  const onToggleWatchlist = (
    item: ITicker,
    inlist: boolean,
    inViewList: boolean
  ) => {
    if (inViewList) {
      const newIds = watchlistViewIds.filter((id) => id !== item.id);
      setWatchlistViewIds(newIds);
    } else {
      const newIds = [...watchlistViewIds, item.id];
      setWatchlistViewIds(newIds);
    }

    if (inlist) {
      props.actionRemoveWatchlist(item.id);
    } else {
      props.actionAddWatchlist(item);
    }
  };

  const rowRenderer = ({
    key, // Unique key within array of rows
    index, // Index of row within collection
    isScrolling, // The List is currently being scrolled
    isVisible, // This row is visible within the List (eg it is not an overscanned row)
    style, // Style object to be applied to row (to position it)
  }: any) => {
    const rawTicker = props.items[index];
    const item = useTicker(rawTicker);
    // const [base, quote] = pair.split("/");
    const even = index % 2 === 0;
    // const noAddress = isNoAddress(props.currentAddress);
    const isActive = props.currentId == item.id;
    const isChangePositive = true; //  item.change > 0.0;
    const rowClassList = `tickers__panel__table__row ${
      isActive ? 'tickers__panel__table__row--active' : ''
    } ${even ? 'tickers__panel__table__row--even' : ''}`;
    const isInWatchlist = props.watchlistTickers.some(
      (ticker) => ticker.id === item.id
    );
    const isInViewWatchlist = watchlistViewIds.includes(item.id);
    const watchlistClassName = isInViewWatchlist
      ? 'active icon-star-active'
      : 'normal icon-star';

    return (
      <div
        className={rowClassList}
        key={key}
        style={style}
        onClick={() => onListItemClicked(rawTicker)}
      >
        <div className='tickers__panel__table__row__pair'>
          <PairIcons
            chain={item.chain}
            pair={item.name}
            base_address={item.base_address}
            quote_address={item.quote_address}
            base_slug={item.base_slug}
            quote_slug={item.quote_slug}
          />
          <Text size={12}>{item.name}</Text>
        </div>
        <div className='tickers__panel__table__row__price'>
          <Text className='text--grey-400' size={12}>
            {getPrice(rawTicker, props.rates)} &nbsp;
            <div
              className={`tickers__panel__table__row__change tickers__panel__table__row__change--${isChangePositive}`}
            >
              &nbsp;
              {/* {item.change ? formatChange(item.change) : ''} */}
            </div>
          </Text>
        </div>
        <div className='tickers__panel__table__row__liquidity'>
          <Text className='text--grey-400' size={12}>
            {rawTicker.kind === enums.ETickerKind.DEX
              ? formatLiquidity(rawTicker.liquidity)
              : ''}
          </Text>
        </div>
        <div className='tickers__panel__table__row__volume'>
          <Text className='text--grey-400' size={12}>
            {formatBaseVolume(rawTicker.volume)}
          </Text>
        </div>
        <div className='tickers__panel__table__row__contract'>
          <Text className='text--navy-600' size={12}>
            {rawTicker.kind === enums.ETickerKind.DEX
              ? formatAddress(item.base_address)
              : ''}
          </Text>
        </div>
        <div className='tickers__panel__table__row__annual_percentage_yield'>
          <Text className='text--grey-400' size={12}>
            {!rawTicker.annual_percentage_yield
              ? ''
              : `${formatAPY(rawTicker.annual_percentage_yield)}`}
          </Text>
        </div>
        <div className='tickers__panel__table__row__exchange'>
          <ExchangeIcon exchange={item.exchange} />
        </div>
        <div
          onClick={(e: any) => {
            e.stopPropagation();
            onToggleWatchlist(rawTicker, isInWatchlist, isInViewWatchlist);
          }}
          className={`tickers__panel__table__row__star tickers__panel__table__row__star--${watchlistClassName}`}
        />
      </div>
    );
  };

  return (
    <div className='tickers__panel'>
      <input
        type='search'
        className='tickers__panel__search text--14'
        id='tickers__panel__search'
        ref={inputRef}
        value={search}
        onChange={(e: any) => setSearch(e.target.value)}
        placeholder='Search...'
        autoComplete='off'
      />
      <div className='tickers__panel__table_container'>
        <TabsSelector
          value={kind}
          onChange={(kind) =>
            props.actionUpdateModalParams({ kind, exchanges: [] })
          }
          isMobile={false}
        />
        <ExchangesSelector
          tab={kind}
          selectedExchanges={props.exchanges}
          setSelectedExchanges={setExchange}
        />

        <div className='tickers__panel__table'>
          <div className='tickers__panel__table__header'>
            {kind === enums.ETickerKind.ALL && (
              <>
                {/**
                 * logic:
                 * if exchanges.length is not 0 AND if there are no dex exchanges picked then show CEX_columns
                 * else (if length is 0 OR at least 1 exchange is dex) show DEX_columns
                 */}
                {props.exchanges.length > 0 &&
                !props.exchanges.some((i) =>
                  helpers.exchanges.DEX_LIST.includes(i as any)
                )
                  ? CEX_columns.map(
                    (
                      column: IColumn // only CEX-like columns
                    ) => (
                      <Column
                        key={column.name}
                        column={column}
                        selected={sort}
                        onSelect={onSelectSort}
                      />
                    )
                  )
                  : DEX_columns.map(
                    (
                      column: IColumn // we use DEX_columns here because this variable has all fields
                    ) => (
                      <Column
                        key={column.name}
                        column={column}
                        selected={sort}
                        onSelect={onSelectSort}
                      />
                    )
                  )}
              </>
            )}
            {kind === enums.ETickerKind.CEX &&
              CEX_columns.map((column: IColumn) => (
                <Column
                  key={column.name}
                  column={column}
                  selected={sort}
                  onSelect={onSelectSort}
                />
              ))}
            {kind === enums.ETickerKind.DEX &&
              DEX_columns.map((column: IColumn) => (
                <Column
                  key={column.name}
                  column={column}
                  selected={sort}
                  onSelect={onSelectSort}
                />
              ))}
          </div>
          {loading && <Loader hide={!props.loading} />}
          {!loading && (
            <div className='tickers__panel__table--virtualized'>
              <AutoSizer>
                {({ width, height }) => (
                  <List
                    width={width - 1}
                    height={height}
                    rowCount={props.items?.length || 0}
                    rowHeight={28}
                    rowRenderer={rowRenderer}
                    onScroll={onScroll}
                    className='tickers__panel__table--perspective'
                  />
                )}
              </AutoSizer>
            </div>
          )}
        </div>
      </div>
      <Resizer
        targetClass='tickers__modal'
        storageKey={LS_TICKERS_HEIGHT}
        topGap={300}
        styleProperty='height'
      />
    </div>
  );
};

const mapStateToProps = (state: IStore) => ({
  rates: state.rates.items,
  tickers: state.tickers,
  watchlistTickers: state.watchlist.tickers,
  currentId: state.current.id,
  search: state.modalSearch.search,
  exchanges: state.modalSearch.exchanges,
  items: state.modalSearch.tickers,
  loading: state.modalSearch.loading,
  page: state.modalSearch.page,
  isNextPageLoading: state.modalSearch.isNextPageLoading,
  kind: state.modalSearch.kind,
});

export default connect(mapStateToProps, {
  actionAddWatchlist,
  actionRemoveWatchlist,
  actionUpdateModalParams,
  actionTickersNextPageModal,
  actionUpdateModal,
  actionCloseModal,
})(TickersPanel);
